import { useEffect, useState } from 'react';
import { MessageSquare, Star, Send, Loader2, RefreshCw, Mail, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('masterToken')}` });

export default function MasterFeedback() {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API}/master/feedback?source=${filter}&limit=200`, { headers: authHeaders() }),
        fetch(`${API}/master/feedback/stats`, { headers: authHeaders() }),
      ]);
      if (r1.ok) setData(await r1.json());
      if (r2.ok) setStats(await r2.json());
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  // Auto-refresh every 30s for live feel
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const sendSurveys = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/master/feedback/send-surveys-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.detail || 'Send failed');
      toast.success(`Sent ${body.sent} survey emails (skipped ${body.skipped})`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const markRead = async (id) => {
    await fetch(`${API}/master/feedback/${id}/mark-read`, { method: 'POST', headers: authHeaders() });
    load();
  };

  const rows = data?.rows || [];

  return (
    <div className="space-y-6" data-testid="master-feedback">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-syne text-2xl font-bold text-white flex items-center gap-2">
            <MessageSquare size={22} className="text-[#A78BFA]" /> Feedback
          </h1>
          <p className="text-[#9CA3AF] text-sm mt-1">Live submissions from vendors and consumers · auto-refreshes every 30s</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-[#9CA3AF] hover:text-white text-xs flex items-center gap-1.5" data-testid="feedback-refresh">
            <RefreshCw size={12} /> Refresh
          </button>
          <button onClick={sendSurveys} disabled={busy} className="px-3 py-2 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50" data-testid="feedback-send-surveys">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} Send pending surveys
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total" v={stats.total} c="text-[#A78BFA]" />
          <Stat label="Unread" v={data?.unread || 0} c="text-amber-400" />
          <Stat label="Avg Overall" v={stats.avg_overall ? `${stats.avg_overall}/5` : '—'} c="text-emerald-400" icon={<Star size={12} />} />
          <Stat label="Avg NPS Q" v={stats.avg_nps ? `${stats.avg_nps}/5` : '—'} c="text-pink-400" />
        </div>
      )}

      {/* Top tally */}
      {stats && (Object.keys(stats.feature_usage_tally || {}).length > 0 || Object.keys(stats.needs_improvement_tally || {}).length > 0) && (
        <div className="grid md:grid-cols-2 gap-4">
          <Tally title="Most-used features" data={stats.feature_usage_tally} accent="text-emerald-300" />
          <Tally title="Top improvement requests" data={stats.needs_improvement_tally} accent="text-amber-300" />
        </div>
      )}

      {/* Filters */}
      <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white/5 border border-white/10">
        {[
          { k: 'all', l: 'All' },
          { k: 'survey', l: '36-hour survey' },
          { k: 'ongoing', l: 'Ongoing ideas' },
        ].map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${filter === f.k ? 'bg-[#8B5CF6]/30 text-white' : 'text-[#9CA3AF] hover:text-white'}`} data-testid={`feedback-filter-${f.k}`}>
            {f.l}
          </button>
        ))}
      </div>

      {/* Submissions list */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-10 text-[#9CA3AF]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-[#64748B] italic">No submissions in this filter yet.</div>
        ) : (
          rows.map((r) => (
            <button
              key={r.submission_id}
              onClick={() => { setSelected(r); if (!r.is_read) markRead(r.submission_id); }}
              className={`w-full text-left bg-[#1E1E3A]/60 border rounded-2xl p-4 hover:border-[#8B5CF6]/40 transition ${r.is_read ? 'border-white/5' : 'border-amber-500/30'}`}
              data-testid={`feedback-row-${r.submission_id}`}
            >
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    {!r.is_read && <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />}
                    <span className="text-white font-semibold text-sm">{r.user_name || '—'}</span>
                    <span className="text-[#9CA3AF] text-xs">· {r.user_email}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    {r.rating_avg != null && (
                      <span className="text-amber-300 text-xs flex items-center gap-1"><Star size={11} fill="currentColor" /> {r.rating_avg.toFixed(1)}/5</span>
                    )}
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${r.source === 'survey' ? 'bg-[#8B5CF6]/20 text-[#A78BFA]' : 'bg-emerald-500/20 text-emerald-300'}`}>
                      {r.source === 'survey' ? '36-hour survey' : 'Ongoing idea'}
                    </span>
                    <span className="text-[#64748B] text-xs">{(r.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                  </div>
                </div>
              </div>
              {/* Preview snippet */}
              {(r.answers?.q15_wishlist || r.answers?.q11_why_joined || r.answers?.q12_visit_more) && (
                <p className="text-[#C4B5FD] text-sm mt-2.5 line-clamp-2">
                  "{(r.answers?.q15_wishlist || r.answers?.q11_why_joined || r.answers?.q12_visit_more || '').slice(0, 200)}"
                </p>
              )}
            </button>
          ))
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setSelected(null)}>
          <div className="bg-[#0E0E23] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="feedback-detail-modal">
            <div className="sticky top-0 bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] p-5 flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold text-lg">{selected.user_name}</h2>
                <p className="text-white/85 text-xs">{selected.user_email} · {(selected.created_at || '').slice(0, 16).replace('T', ' ')}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-white/80 hover:text-white">✕</button>
            </div>
            <div className="p-5 space-y-3">
              {Object.entries(selected.answers || {}).map(([k, v]) => (
                <div key={k} className="bg-white/[0.02] border border-white/5 rounded-xl p-3">
                  <div className="text-[#A78BFA] text-[11px] uppercase tracking-wider font-bold mb-1.5">{k}</div>
                  <div className="text-white text-sm">
                    {typeof v === 'number' ? <span className="flex items-center gap-1"><Star size={14} className="text-amber-300" fill="currentColor" /> {v}/5</span>
                      : Array.isArray(v) ? v.join(' · ')
                      : <span className="whitespace-pre-wrap">{String(v)}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, v, c, icon }) {
  return (
    <div className="bg-[#1E1E3A] border border-white/10 rounded-2xl p-4">
      <div className="text-[#9CA3AF] text-xs uppercase tracking-wider flex items-center gap-1">{icon}{label}</div>
      <div className={`text-2xl font-bold mt-1 ${c}`}>{v}</div>
    </div>
  );
}

function Tally({ title, data, accent }) {
  const sorted = Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, 6);
  if (sorted.length === 0) return null;
  return (
    <div className="bg-[#1E1E3A] border border-white/10 rounded-2xl p-4">
      <p className={`${accent} text-xs uppercase tracking-wider font-bold mb-2`}>{title}</p>
      <ul className="space-y-1.5">
        {sorted.map(([name, count]) => (
          <li key={name} className="flex items-center justify-between text-sm">
            <span className="text-white truncate mr-2">{name}</span>
            <span className="text-[#9CA3AF] font-mono text-xs">{count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
